import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell, TableHead,
  TableRow, Button, Alert, IconButton, Chip, Tooltip, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, MenuItem, Avatar, Grid,
} from '@mui/material';
import { Add, Home, Assessment, Refresh, Gavel, Search } from '@mui/icons-material';
import axios from 'axios';
import { useUnsavedGuard } from '../hooks/useUnsavedGuard';
import DisciplinaryCard from './DisciplinaryCard';

const API = process.env.REACT_APP_API_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const fmtDate = (d) => d ? new Date(d).toLocaleDateString() : '—';
const STATUS_COLOR = { Open: 'warning', InProgress: 'info', Closed: 'success' };
const SEV_COLOR = { Low: 'default', Medium: 'info', High: 'warning', Critical: 'error' };

const emptyCase = {
  employeeNo: '', offenceType: 'Minor', offenceCategory: '', severity: 'Medium',
  offenceDate: '', description: '', witnesses: '', comments: '', recommendations: '',
};

const DisciplinaryList = () => {
  const navigate = useNavigate();
  const [cases, setCases] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [status, setStatus] = useState('');
  const [offenceType, setOffenceType] = useState('');
  const [search, setSearch] = useState('');
  const [error, setError]   = useState('');
  const [openId, setOpenId] = useState(null);
  const [newDlg, setNewDlg] = useState(null);
  const { attemptClose, discardDialog } = useUnsavedGuard({ open: Boolean(newDlg), value: newDlg, onClose: () => setNewDlg(null) });
  const [newErr, setNewErr] = useState('');

  const load = useCallback(async () => {
    try {
      const q = new URLSearchParams();
      if (status) q.set('status', status);
      if (offenceType) q.set('offenceType', offenceType);
      const r = await axios.get(`${API}/api/disciplinary/cases?${q}`, { headers: hdrs() });
      setCases(r.data); setError('');
    } catch (e) { setError(e.response?.data?.error || 'Failed to load cases'); }
  }, [status, offenceType]);
  useEffect(() => { load(); }, [load]);

  const openNew = async () => {
    try { const r = await axios.get(`${API}/api/employees`, { headers: hdrs() }); setEmployees(r.data.filter(e => e.status === 'Active')); }
    catch { setEmployees([]); }
    setNewErr(''); setNewDlg({ ...emptyCase });
  };

  const createCase = async () => {
    setNewErr('');
    try {
      const body = { ...newDlg, witnesses: newDlg.witnesses ? newDlg.witnesses.split(',').map(s => s.trim()).filter(Boolean) : [] };
      const r = await axios.post(`${API}/api/disciplinary/cases`, body, { headers: hdrs() });
      setNewDlg(null); await load(); setOpenId(r.data._id);
    } catch (e) { setNewErr(e.response?.data?.error || 'Could not create case'); }
  };

  const filtered = cases.filter(c => {
    const q = search.trim().toLowerCase();
    return !q || (c.employeeName || '').toLowerCase().includes(q) || (c.employeeNo || '').toLowerCase().includes(q) || (c.caseNo || '').toLowerCase().includes(q);
  });

  return (
    <Box sx={{ maxWidth: 1150, mx: 'auto', mt: 4, mb: 4, px: 2 }}>
      <Card elevation={3}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={1}>
            <Box display="flex" alignItems="center" gap={1}>
              <Gavel color="primary" />
              <Typography variant="h5" color="primary">Disciplinary Cases</Typography>
              <Chip label={filtered.length} size="small" color="primary" />
            </Box>
            <Box display="flex" gap={1}>
              <Tooltip title="Reports"><IconButton onClick={() => navigate('/hr/disciplinary/reports')}><Assessment /></IconButton></Tooltip>
              <Tooltip title="Refresh"><IconButton onClick={load}><Refresh /></IconButton></Tooltip>
              <Tooltip title="HR Home"><IconButton onClick={() => navigate('/hr')}><Home /></IconButton></Tooltip>
              <Button variant="contained" startIcon={<Add />} onClick={openNew}>New Case</Button>
            </Box>
          </Box>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <Box display="flex" gap={1.5} mb={2} flexWrap="wrap">
            <TextField select size="small" label="Status" value={status} onChange={e => setStatus(e.target.value)} sx={{ minWidth: 150 }}>
              <MenuItem value="">All statuses</MenuItem>
              {['Open', 'InProgress', 'Closed'].map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </TextField>
            <TextField select size="small" label="Offence Type" value={offenceType} onChange={e => setOffenceType(e.target.value)} sx={{ minWidth: 150 }}>
              <MenuItem value="">All types</MenuItem>
              {['Minor', 'Major', 'Gross'].map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </TextField>
            <TextField size="small" placeholder="Search employee / case…" value={search} onChange={e => setSearch(e.target.value)}
              InputProps={{ startAdornment: <Search fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} /> }} sx={{ minWidth: 220 }} />
          </Box>

          <Table size="small">
            <TableHead><TableRow sx={{ bgcolor: 'grey.50' }}>
              <TableCell><strong>Employee</strong></TableCell>
              <TableCell><strong>Case</strong></TableCell>
              <TableCell><strong>Offence</strong></TableCell>
              <TableCell><strong>Severity</strong></TableCell>
              <TableCell><strong>Date</strong></TableCell>
              <TableCell><strong>Current Stage</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
            </TableRow></TableHead>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow><TableCell colSpan={7} align="center"><Typography variant="body2" color="text.secondary" py={2}>No disciplinary cases.</Typography></TableCell></TableRow>
              ) : filtered.map(c => (
                <TableRow key={c._id} hover sx={{ cursor: 'pointer' }} onClick={() => setOpenId(c._id)}>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1.5}>
                      <Avatar sx={{ width: 30, height: 30, bgcolor: '#c6282822', color: '#c62828', fontSize: 13, fontWeight: 700 }}>{(c.employeeName || '?').charAt(0)}</Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight={600} lineHeight={1.2}>{c.employeeName}</Typography>
                        <Typography variant="caption" color="text.secondary">{c.employeeNo} · {c.department || '—'}</Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell><Typography variant="caption">{c.caseNo}</Typography></TableCell>
                  <TableCell><Chip label={c.offenceType} size="small" color={c.isGross ? 'error' : 'default'} variant={c.isGross ? 'filled' : 'outlined'} /></TableCell>
                  <TableCell><Chip label={c.severity} size="small" color={SEV_COLOR[c.severity] || 'default'} variant="outlined" /></TableCell>
                  <TableCell>{fmtDate(c.offenceDate)}</TableCell>
                  <TableCell><Typography variant="caption">{c.currentStage || '—'}</Typography></TableCell>
                  <TableCell><Chip label={c.status} size="small" color={STATUS_COLOR[c.status] || 'default'} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* New case dialog */}
      {discardDialog}
      <Dialog open={Boolean(newDlg)} onClose={attemptClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><Gavel color="primary" /> New Disciplinary Case</DialogTitle>
        <DialogContent dividers>
          {newErr && <Alert severity="error" sx={{ mb: 2 }}>{newErr}</Alert>}
          {newDlg && (
            <Grid container spacing={2.5} sx={{ pt: 1 }}>
              <Grid item xs={12}>
                <TextField select label="Employee *" value={newDlg.employeeNo} onChange={e => setNewDlg({ ...newDlg, employeeNo: e.target.value })} fullWidth>
                  {employees.map(e => <MenuItem key={e.employeeNo} value={e.employeeNo}>{e.employeeNo} — {e.firstName} {e.lastName}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField select label="Offence Type *" value={newDlg.offenceType} onChange={e => setNewDlg({ ...newDlg, offenceType: e.target.value, severity: e.target.value === 'Gross' ? 'Critical' : newDlg.severity })} fullWidth>
                  {['Minor', 'Major', 'Gross'].map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField select label="Severity" value={newDlg.severity} onChange={e => setNewDlg({ ...newDlg, severity: e.target.value })} fullWidth>
                  {['Low', 'Medium', 'High', 'Critical'].map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={4}><TextField type="date" label="Offence Date *" InputLabelProps={{ shrink: true }} value={newDlg.offenceDate} onChange={e => setNewDlg({ ...newDlg, offenceDate: e.target.value })} fullWidth /></Grid>
              <Grid item xs={12}><TextField label="Category" value={newDlg.offenceCategory} onChange={e => setNewDlg({ ...newDlg, offenceCategory: e.target.value })} fullWidth size="small" placeholder="e.g. Lateness, Insubordination, Theft" /></Grid>
              <Grid item xs={12}><TextField label="Description *" value={newDlg.description} onChange={e => setNewDlg({ ...newDlg, description: e.target.value })} fullWidth size="small" multiline rows={2} /></Grid>
              <Grid item xs={12}><TextField label="Witnesses (comma-separated)" value={newDlg.witnesses} onChange={e => setNewDlg({ ...newDlg, witnesses: e.target.value })} fullWidth size="small" /></Grid>
              <Grid item xs={12}><TextField label="Comments" value={newDlg.comments} onChange={e => setNewDlg({ ...newDlg, comments: e.target.value })} fullWidth size="small" /></Grid>
              <Grid item xs={12}><TextField label="Recommendations" value={newDlg.recommendations} onChange={e => setNewDlg({ ...newDlg, recommendations: e.target.value })} fullWidth size="small" /></Grid>
              {newDlg.offenceType === 'Gross' && <Grid item xs={12}><Alert severity="warning">Gross misconduct skips the progressive ladder and may lead to direct dismissal.</Alert></Grid>}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={attemptClose}>Cancel</Button>
          <Button variant="contained" disabled={!newDlg?.employeeNo || !newDlg?.offenceDate || !newDlg?.description} onClick={createCase}>Create</Button>
        </DialogActions>
      </Dialog>

      {openId && <DisciplinaryCard caseId={openId} onClose={() => setOpenId(null)} onChanged={load} />}
    </Box>
  );
};

export default DisciplinaryList;
